import React, { useEffect, useState } from 'react';
import { Form, Skeleton } from 'antd';
import { Modal, Message } from '@bluevision/components';
import { Input, Button, Alert } from '@bluevision/components';
import { useTranslation } from 'react-i18next';

import { fetchSubmitIdentificationInfo } from '@/services/identification';
import { queryCurrent } from '@/services/authority/login';

import styles from './style.less';

export interface RealNameIdentificationProps {
  /** 认证弹窗显隐 */
  visible: boolean;
  /** 认证弹窗关闭回调 */
  onCancel: () => void;
  /** 实名认证成功回调 */
  onIdentifySuccess?: () => void;
}

const Identification: React.FC<RealNameIdentificationProps> = (props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { visible, onIdentifySuccess, onCancel } = props;
  // 认证错误信息
  const [errorMessage, setErrorMessage] = useState<string>();
  // 认证时loading
  const [identityLoading, setIdentityLoading] = useState(false);
  // fetch loading
  const [fetchLoading, setFetchLoading] = useState(true);

  // 关闭弹窗时清空错误信息
  useEffect(() => {
    if (!visible) {
      setErrorMessage('');
    }
  }, [visible]);

  // 打开弹窗时获取认证信息
  useEffect(() => {
    if (visible) {
      setFetchLoading(true);
      queryCurrent()
        .then(([res, error]) => {
          // 返回的认证信息是一个数组，只取第一个
          if (!error) {
            form.setFieldsValue(res?.verifyInfo ?? {});
          }
        })
        .finally(() => {
          setFetchLoading(false);
        });
    }
  }, [form, visible]);

  // 提交认证信息
  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      setIdentityLoading(true);
      fetchSubmitIdentificationInfo(values)
        .then(([, error]) => {
          if (!error) {
            Message.success(t('submit_identification_success'));
            if (onIdentifySuccess) {
              onIdentifySuccess();
            }
            onCancel();
          } else {
            setErrorMessage(error ?? t('submit_identification_error'));
          }
        })
        .finally(() => {
          setIdentityLoading(false);
        });
    });
  };

  return (
    <Modal
      destroyOnClose
      open={visible}
      size='small'
      title={t('real_name_authentication')}
      footer={null}
      onCancel={onCancel}
    >
      <Skeleton loading={fetchLoading} active>
        <Form layout='vertical' autoComplete='off' form={form} preserve={false}>
          <Form.Item
            className={styles['form-item']}
            label={t('name')}
            name='contactName'
            rules={[
              { required: true, message: t('name_cannot_be_empty') },
              { pattern: /^[^\s]+$/, message: t('name_cannot_contain_spaces') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            className={styles['form-item']}
            label={t('id_number')}
            name='idNumber'
            rules={[
              { required: true, message: t('id_number_cannot_be_empty') },
              { pattern: /^\d{17}[\dX]$/, message: t('please_enter_the_correct_id_number') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            className={styles['form-item']}
            label={t('mobile_phone_number')}
            name='phone'
            rules={[
              { required: true, message: t('phone_number_cannot_be_empty') },
              {
                pattern: /^1[3-9]\d{9}$/,
                message: t('please_enter_the_correct_formatted_phone_number'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
        {/* Message组件有内联样式只能通过传递style进行覆盖 */}
        {errorMessage && <Alert showIcon type='error' message={errorMessage} />}
        <div className={styles['form-footer']}>
          <Button size='large' onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button
            loading={identityLoading}
            disabled={identityLoading}
            type='primary'
            size='large'
            onClick={handleFormSubmit}
          >
            {t('identification')}
          </Button>
        </div>
      </Skeleton>
    </Modal>
  );
};

export default Identification;
